import { useEffect, useState } from "react";
import ToggleSwitch from "../../components/ToggleSwitch";
import DataTable, { Column } from "../../components/DataTable";
import SchoolBranchApis from "../../api/schoolBranchApis";
import { AcademicCycle } from "../../types/schoolBranchType";
import { toast } from "sonner";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

export default function AcademicCycles() {
  const [academicCycles, setAcademicCycles] = useState<AcademicCycle[]>([]);

  const [loading, setLoading] = useState<boolean>(false);

  const branchId = useSelector(
    (state: RootState) => state.access.schoolBranch?.schoolBranchId,
  );

  const fetchAcademicCycles = async () => {
    try {
      setLoading(true);
      const response = await SchoolBranchApis.getBranchAcademicCycle(branchId);
      setAcademicCycles(response.data);
    } catch (err) {
      toast.error("Failed to load academic cycles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAcademicCycles();
  }, [branchId]);

  const handleToggle = async (academicCycleId: AcademicCycle) => {
    try {
      setLoading(true);
      await SchoolBranchApis.toggleAcademicCycle(
        branchId,
        academicCycleId.academicCycleId!,
      );
      const message = academicCycleId.active
        ? "Academic Cycle disabled successfully"
        : "Academic Cycle successfully";

      toast.success(message);
      await fetchAcademicCycles();
    } catch (error) {
      toast.error("Feature toggle failed");
    } finally {
      setLoading(false);
    }
  };

  const columns: Column<AcademicCycle>[] = [
    {
      label: "Academic Cycle Name",
      accessor: "academicCycleName",
      render: (item) => (
        <div className="font-bold text-sm text-neutral-400">
          {item.academicCycleName}
        </div>
      ),
    },
    {
      label: "Enable/Disable",
      accessor: "enable",
      searchable: false,
      render: (item) => (
        <ToggleSwitch
          defaultChecked={item.active}
          onToggle={() => handleToggle(item)}
        />
      ),
    },
  ];

  return (
    <div className="px-4 md:px-9 py-7 flex flex-col gap-8 overflow-x-auto">
      <span className="text-xs font-medium text-error-500">
        *Please enable the academic cycles available at your school
      </span>

      <div className="overflow-x-auto">
        <div className="hidden lg:grid lg:grid-cols-2 lg:gap-x-12">
          {/* Split into two columns on large screens */}
          <DataTable
            columns={columns}
            data={academicCycles.slice(0, Math.ceil(academicCycles.length / 2))}
            isLoading={loading}
          />
          <DataTable
            columns={columns}
            data={academicCycles.slice(Math.ceil(academicCycles.length / 2))}
            isLoading={loading}
          />
        </div>

        {/* Single table for small screens */}
        <div className="lg:hidden">
          <DataTable
            columns={columns}
            data={academicCycles}
            isLoading={loading}
          />
        </div>
      </div>
    </div>
  );
}
